import { useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { CampaignForm, type CampaignFormSubmitData } from './campaign-form';
import { CAMPAIGN_CREATE_MUTATION } from './campaigns.gql.tsx';
import {
	buildCreateCampaignInput,
	generateIdempotentCampaignId
} from './campaign-input';

interface CloneData {
	campaignId?: string; // Pre-generated ID from clone function
	name?: string;
	description?: string | null;
	startDate?: string;
	endDate?: string;
	isActive?: boolean;
}

interface LocationState {
	cloneData?: CloneData;
}

export const CampaignsCreate = () => {
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const go = useGo();
	const location = useLocation();
	const locationState = location.state as LocationState | null;
	const cloneData = locationState?.cloneData;

	// Use the pre-generated campaignId from clone data, or generate a new one
	const campaignId = useMemo(
		() => cloneData?.campaignId ?? generateIdempotentCampaignId(),
		[cloneData?.campaignId]
	);

	const [createCampaign, { loading: isSubmitting }] = useMutation(
		CAMPAIGN_CREATE_MUTATION
	);

	useEffect(() => {
		setPageHeader({
			title: cloneData ? 'Clone campaign' : 'Create campaign',
			description: cloneData
				? 'Create a new campaign based on an existing one'
				: 'Launch a new marketing campaign to group vouchers',
			showBackButton: true,
			onBackClick: () => go({ to: '/campaigns' })
		});

		return () => {
			resetPageHeader();
		};
	}, [cloneData, go, resetPageHeader, setPageHeader]);

	const handleSubmit = async (values: CampaignFormSubmitData) => {
		const payload = buildCreateCampaignInput(values, campaignId);

		try {
			const { data } = await createCampaign(
				getMutationOptions('campaigns', { input: payload })
			);

			const message = extractCheckResultError(data?.createCampaign);
			if (message) {
				showMutationErrorToast('campaigns', 'create', { message });
				return;
			}

			showMutationSuccessToast('campaigns', 'create');
			// Navigate back with the newly created campaign ID for selection
			go({
				to: '/campaigns',
				state: { selectCampaignId: campaignId }
			});
		} catch (error) {
			showMutationErrorToast('campaigns', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			<CampaignForm
				key="create-campaign-form"
				mode={cloneData ? 'clone' : 'create'}
				initialData={cloneData ? {
					name: cloneData.name ?? '',
					description: cloneData.description ?? '',
					startDate: cloneData.startDate ?? '',
					endDate: cloneData.endDate ?? '',
					isActive: cloneData.isActive ?? true
				} : undefined}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle={cloneData ? 'Clone campaign' : 'Create campaign'}
			/>
		</FormPageLayout>
	);
};
